import { useState } from "react";
import { WHAT_IF_EXTRA_DEFAULT } from "web_myplann/Goals/constants";
import { calculateExtraContributionImpact } from "web_myplann/Goals/Helpers/extraContributionImpact";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import { formatMonthsSaved } from "web_myplann/Goals/Helpers/formatMonthsSaved";
import { getGoalMonthlySavings } from "web_myplann/Goals/Helpers/goalMonthlySavings";
import { useGoalAdvisorChat } from "web_myplann/Goals/Hooks/useGoalAdvisorChat";
import {
	useWhatIfScenarios,
	type WhatIfScenario,
	type WhatIfScenarioInfo,
} from "web_myplann/Goals/Hooks/useWhatIfScenarios";
import type { ExtraContributionImpact } from "web_myplann/Goals/Types/ExtraContributionImpact";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type ExtraContributionPlan = {
	isVisible: boolean;
	extraAmount: number;
	setExtraAmount: (value: number) => void;
	activeScenario: WhatIfScenario;
	setActiveScenario: (scenario: WhatIfScenario) => void;
	scenarios: WhatIfScenario[];
	scenarioInfo: Record<WhatIfScenario, WhatIfScenarioInfo>;
	impact: ExtraContributionImpact;
	savedLabel: string;
	openAdvisorChat: () => void;
};

export const useExtraContributionPlan = (
	goal: GoalDetail,
): ExtraContributionPlan => {
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();
	const openChat = useGoalAdvisorChat(goal);

	const [extraAmount, setExtraAmount] = useState(WHAT_IF_EXTRA_DEFAULT);
	const monthlySavings = getGoalMonthlySavings(goal);
	const canFinishEarlier = monthlySavings > 0;
	const hasPinnedOrPlannedContract =
		goal.portfolios.length > 0 || goal.futurePortfolios.length > 0;
	const [activeScenario, setActiveScenario] = useState<WhatIfScenario>(
		canFinishEarlier ? "earlier" : "more",
	);

	const impact = calculateExtraContributionImpact(
		{
			dueYear: goal.dueYear,
			savingsTarget: goal.savingsTarget,
			monthlySavings,
		},
		extraAmount,
	);
	const savedLabel = formatMonthsSaved(impact.monthsSaved, t);

	const { scenarios, scenarioInfo } = useWhatIfScenarios({
		canFinishEarlier,
		savedLabel,
		extraTotal: impact.extraTotal,
		currencyCode: goal.currencyCode,
	});

	const openAdvisorChat = () =>
		openChat(
			t(Resources.Goals.Detail.WhatIf.advisorMessage, {
				goal: goal.name,
				extra: formatAmount(extraAmount, goal.currencyCode),
			}),
		);

	return {
		isVisible:
			!goal.completed &&
			goal.savingsTarget > 0 &&
			impact.yearsLeft > 0 &&
			hasPinnedOrPlannedContract,
		extraAmount,
		setExtraAmount,
		activeScenario: scenarios.includes(activeScenario)
			? activeScenario
			: scenarios[0],
		setActiveScenario,
		scenarios,
		scenarioInfo,
		impact,
		savedLabel,
		openAdvisorChat,
	};
};
